/**
 * "What a month buys": the calculator's arithmetic, state and words, as one hook.
 *
 * ONE copy, read by today's calculator (./WorkUnitCalculator.tsx, on /pricing and /subscription) and by the pricing page's
 * rebuild in the new design, which may not import a styled-components module (design-system §7b: behaviour is lifted
 * into a hook, never copied). Lifted on 2026-09-30; nothing it computes or says changed.
 *
 * Every number comes from src/config/workUnits.ts: each row starts at TYPICAL_MONTH.count, each rate is
 * TYPICAL_MONTH.wuEach, and each slider's MAXIMUM is singleProductCeiling() (one Prest0n's month ÷ that rate), so
 * dragging one row to the end shows what a month of only that product holds.
 */
import React from 'react';
import {
  MONTHLY_WU_PER_PRESTON,
  TYPICAL_MONTH,
  WU_LABEL,
  singleProductCeiling,
  typicalMonthLineWu,
} from '../../config/workUnits';

export type MonthCalcCounts = Record<string, number>;

/** A typical month: every row at its worked-example count. */
export const TYPICAL_MONTH_COUNTS: MonthCalcCounts = TYPICAL_MONTH.reduce<MonthCalcCounts>(
  (acc, r) => ({ ...acc, [r.key]: r.count }),
  {},
);

const grouped = (n: number) => n.toLocaleString('en-US');

/** The calculator's fixed words. */
export const MONTH_CALCULATOR_WORDS = {
  /** The trigger that opens the calculator. */
  launch: 'see what a month buys',
  /** The dialog's title, and its accessible name. */
  title: 'what a month buys',
  dialogLabel: 'what a month of work buys',
  close: 'close',
  /** The foot of the meter while the month still fits (with the name kept in its case). */
  footFits: { lead: 'one ', name: 'Prest0n', tail: ' professional, one month. drag any row to see what a month of only that work holds.' },
  footOver:
    "past your month, work keeps going — overtime bills at the standard rate, and nothing beyond your committed team charges without your administrator's approval.",
  reset: 'reset to a typical month',
  /** The create-account row inside the dialog, on a public page. */
  signup: 'create your account — over 100 jobs on us',
  signupNote: '14-day evaluation · no credit card · about two minutes to set up',
} as const;

export interface MonthCalcRow {
  key: string;
  /** The shipped plural, e.g. "drafted documents". */
  item: string;
  value: number;
  max: number;
  /** How far the slider's fill runs, 0..100. */
  fillPct: number;
  /** "1,200" */
  valueText: string;
  /** "360 pr0" */
  lineText: string;
  /** What a screen reader hears for the slider's value: "120 drafted documents". */
  ariaValueText: string;
}

export function useMonthCalculator() {
  const [counts, setCounts] = React.useState<MonthCalcCounts>(TYPICAL_MONTH_COUNTS);

  const total = TYPICAL_MONTH.reduce(
    (sum, r) => sum + typicalMonthLineWu({ count: counts[r.key] ?? 0, wuEach: r.wuEach }),
    0,
  );
  const over = total > MONTHLY_WU_PER_PRESTON;
  const pct = Math.min(100, (total / MONTHLY_WU_PER_PRESTON) * 100);
  const remaining = MONTHLY_WU_PER_PRESTON - total;
  const isPreset = TYPICAL_MONTH.every((r) => (counts[r.key] ?? 0) === r.count);

  const rows: MonthCalcRow[] = TYPICAL_MONTH.map((r) => {
    const value = counts[r.key] ?? 0;
    const max = singleProductCeiling(r.wuEach);
    return {
      key: r.key,
      item: r.item,
      value,
      max,
      fillPct: (value / max) * 100,
      valueText: grouped(value),
      lineText: `${grouped(typicalMonthLineWu({ count: value, wuEach: r.wuEach }))} ${WU_LABEL}`,
      ariaValueText: `${value} ${r.item}`,
    };
  });

  const setCount = React.useCallback((key: string, value: number) => {
    setCounts((c) => ({ ...c, [key]: value }));
  }, []);
  const reset = React.useCallback(() => setCounts(TYPICAL_MONTH_COUNTS), []);

  return {
    rows,
    setCount,
    reset,
    total,
    over,
    pct,
    remaining,
    isPreset,
    /** "1,540" */
    totalText: grouped(Math.round(total)),
    /** "of 2,200 pr0" */
    ofText: `of ${grouped(MONTHLY_WU_PER_PRESTON)} ${WU_LABEL}`,
    /** "660 pr0 still free", or "140 pr0 into overtime" */
    noteText: over
      ? `${grouped(Math.abs(remaining))} ${WU_LABEL} into overtime`
      : `${grouped(remaining)} ${WU_LABEL} still free`,
    /** The meter's value, capped at the month. */
    meterNow: Math.round(Math.min(total, MONTHLY_WU_PER_PRESTON)),
    meterMax: MONTHLY_WU_PER_PRESTON,
  };
}
